import { createContext, useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { useSelector } from 'react-redux';
import { subscribeToRoomInboxEvents } from '../../actionCable/channels/roomInboxEvents.js';
import { useRealtimeConsumer } from '../../actionCable/useRealtimeConsumer.js';
import { fetch_request } from '../../service/request.js';
import { decryptHistory } from '../messenger-v2/runtime/messengerV2HistoryCrypto.js';
import {
  createInitialRoomInboxState,
  normalizeRoomInboxSnapshot,
  roomInboxReducer,
} from './roomInboxState.js';

const RoomInboxContext = createContext(null);

const decryptRoomInboxPreviews = async (snapshot) => {
  const rooms = await Promise.all(
    snapshot.rooms.map(async (room) => {
      const encryptedPreview = room.encryptedPreview;
      if (!encryptedPreview || encryptedPreview.algorithm !== 'AES-GCM') return room;

      const latestMessagePreview = await decryptHistory({
        conversationId: room.conversationId,
        ciphertextB64: encryptedPreview.ciphertextB64,
        ivB64: encryptedPreview.ivB64,
      }).catch(() => null);
      return latestMessagePreview ? { ...room, latestMessagePreview } : room;
    }),
  );

  return {
    ...snapshot,
    rooms,
    roomsById: Object.fromEntries(rooms.map((room) => [room.roomId, room])),
  };
};

const loadRoomInboxSnapshot = async (signal) => {
  const response = await fetch_request('/api/rooms/inbox', {
    method: 'GET',
    cache: 'no-store',
    signal,
  });
  if (!response.ok) {
    const error = new Error(`Room inbox request failed with HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }
  const snapshot = normalizeRoomInboxSnapshot(await response.json());
  return decryptRoomInboxPreviews(snapshot);
};

export const RoomInboxProvider = ({ children }) => {
  const isAuthorized = useSelector((state) => state?.auth?.isAuthorized === true);
  const accountId = useSelector((state) => state?.user?.user?.id ?? null);
  const cable = useRealtimeConsumer();
  const [state, dispatch] = useReducer(roomInboxReducer, undefined, createInitialRoomInboxState);
  const generationRef = useRef(0);
  const acceptedVersionRef = useRef(0);
  const requestedVersionRef = useRef(0);
  const inFlightRef = useRef(null);
  const abortControllerRef = useRef(null);

  const refresh = useCallback(
    (minimumVersion = 0) => {
      if (!isAuthorized || !accountId) return Promise.resolve(null);

      requestedVersionRef.current = Math.max(
        requestedVersionRef.current,
        Number(minimumVersion) || 0,
      );
      if (inFlightRef.current) return inFlightRef.current;

      const generation = generationRef.current;
      const execute = async () => {
        let attempts = 0;
        let latestSnapshot = null;

        while (attempts < 2) {
          attempts += 1;
          const controller = new AbortController();
          abortControllerRef.current = controller;
          dispatch({ type: 'loading' });

          try {
            const snapshot = await loadRoomInboxSnapshot(controller.signal);
            if (generationRef.current !== generation) return null;
            latestSnapshot = snapshot;
            if (snapshot.activityVersion >= acceptedVersionRef.current) {
              acceptedVersionRef.current = snapshot.activityVersion;
              dispatch({ type: 'success', snapshot });
            }
            if (snapshot.activityVersion >= requestedVersionRef.current) break;
          } catch (error) {
            if (error?.name !== 'AbortError' && generationRef.current === generation) {
              dispatch({ type: 'error', error });
            }
            break;
          }
        }

        return latestSnapshot;
      };

      const request = execute().finally(() => {
        if (inFlightRef.current === request) inFlightRef.current = null;
      });
      inFlightRef.current = request;
      return request;
    },
    [accountId, isAuthorized],
  );

  useEffect(() => {
    generationRef.current += 1;
    acceptedVersionRef.current = 0;
    requestedVersionRef.current = 0;
    abortControllerRef.current?.abort();
    inFlightRef.current = null;
    dispatch({ type: 'reset' });

    if (!isAuthorized || !accountId) return undefined;
    void refresh();

    return () => {
      generationRef.current += 1;
      abortControllerRef.current?.abort();
      inFlightRef.current = null;
    };
  }, [accountId, isAuthorized, refresh]);

  useEffect(() => {
    if (!isAuthorized || !accountId || !cable) return undefined;

    return subscribeToRoomInboxEvents(cable, accountId, {
      onConnected: () => void refresh(),
      onReceive: (payload) => {
        const version = Number(payload?.room_inbox?.activity_version) || 0;
        const roomEvent = payload?.room_event;
        if (roomEvent?.kind && version > 0) {
          dispatch({
            type: 'lifecycle',
            event: {
              kind: String(roomEvent.kind),
              roomId: roomEvent.room_id == null ? null : String(roomEvent.room_id),
              conversationId:
                roomEvent.conversation_id == null ? null : String(roomEvent.conversation_id),
              archivedChatEnabled:
                roomEvent.archived_chat_enabled == null
                  ? null
                  : roomEvent.archived_chat_enabled === true,
              occurredAt: roomEvent.occurred_at || null,
              version,
            },
          });
        }
        if (version <= acceptedVersionRef.current) return;
        void refresh(version);
      },
    });
  }, [accountId, cable, isAuthorized, refresh]);

  useEffect(() => {
    if (!isAuthorized || !accountId) return undefined;

    const reconcileVisibleInbox = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener('focus', reconcileVisibleInbox);
    document.addEventListener('visibilitychange', reconcileVisibleInbox);
    return () => {
      window.removeEventListener('focus', reconcileVisibleInbox);
      document.removeEventListener('visibilitychange', reconcileVisibleInbox);
    };
  }, [accountId, isAuthorized, refresh]);

  const value = {
    ...state,
    ...state.snapshot,
    refresh,
  };

  return <RoomInboxContext.Provider value={value}>{children}</RoomInboxContext.Provider>;
};

export const useRoomInbox = () => {
  const context = useContext(RoomInboxContext);
  if (!context) throw new Error('useRoomInbox must be used inside RoomInboxProvider');
  return context;
};
